<template>
  <div>
    <div v-if="canEdit" class="flex-align__end m-b-16">
      <el-button
        type="danger"
        plain
        class="delete-button"
        @click="handleBatchDelete"
      >
        批量删除
      </el-button>
      <div>
        <slot name="materialOperate">
          <!-- 其他操作 -->
          <slot name="otherOperate"></slot>

          <download-file
            v-if="canAdd"
            class="m-l-10"
            :download-link="`/resources/${downloadLink}.xlsx`"
          >
            <div class="flex-center">
              <el-button plain>下载模板</el-button>
            </div>
          </download-file>
          <div
            v-if="canAdd"
            style="display: inline-block;"
            class="m-l-10"
            @click="handleImport"
          >
            <el-button type="primary">批量导入</el-button>

            <input
              v-show="false"
              ref="fileupload"
              type="file"
              name="fileupload"
              :value="uploadFile"
              @change.stop="fileChange"
            />
          </div>
        </slot>
      </div>
    </div>

    <material-item-table
      ref="materialItemTable"
      v-loading="loading"
      element-loading-text="导入中"
      :table-list="materialList"
      :echo-list="selectedMaterials"
      pagination
      :page-sizes="pageSizes"
      :selection="canEdit"
      :table-config-fun="getTableConfigList"
      :show-operation="false"
      :table-id="tableId"
      @selectionChange="handleMaterialTableSelectionChange"
    >
      <!--除附件和操作的其他通用插槽-->
      <template
        v-for="item in slotList"
        v-slot:[item.slotName]="{ row, $index }"
      >
        <!--除附件和操作外的通用插槽-->
        <slot
          v-if="item.slotName !== 'attachment' && item.slotName !== 'operation'"
          :name="item.slotName"
          :row="row"
          :$index="$index"
        ></slot>
      </template>

      <!--附件插槽-->
      <template v-slot:attachment="{ row }">
        <upload
          v-if="row[attachmentField.prop]"
          :props-data="{
            groupIdBefore: row[attachmentField.prop],
            disabled: true
          }"
        />
        <span v-else :key="item.slotName">-</span>
      </template>

      <!--操作插槽-->
      <template v-slot:operation="{ row, $index }">
        <el-button
          v-if="canEdit"
          class="primary-button"
          type="text"
          @click="addOrEditMaterialInfo('edit', row)"
        >
          编辑
        </el-button>
        <el-button
          v-if="canEdit"
          class="danger-button"
          type="text"
          @click="handleMaterialRemove(row, $index)"
        >
          删除
        </el-button>
        <el-button type="text" @click="handleMaterialLook(row)">
          查看详情
        </el-button>
      </template>
    </material-item-table>

    <!--      点击添加品目-->
    <div
      v-if="canEdit && canAdd"
      class="table-btn flex-center m-t-16"
      @click="addOrEditMaterialInfo('add')"
    >
      <i class="el-icon-plus"></i>
      添加品目
    </div>

    <!--    显示上传品目总价-->
    <div v-if="!isSupplier && showSummary" class="all-count m-t-20 m-l-20">
      <div class="flex-align">
        <div class="all-count__label">全部品目总价汇总：</div>
        <div class="all-count__content">{{ totalFee | priceInThousands }}</div>
        <div v-if="totalFee" class="all-count__unit">元</div>
        <money-in-cn class="m-l-16" :money="totalFee" />
      </div>
    </div>

    <!--    添加或修改品目信息的弹窗-->
    <edit-dialog
      v-model="showEditDialog"
      :title="editDialogTitle"
      :material-info="materialInfo"
      :upload-params="defaultUploadParams"
      :material-field="tableConfig"
      :item-code-required="itemCodeRequired"
      :is-factory-disabled="isFactoryDisabled"
      :is-address-select="isAddressSelect"
      :address-required="addressRequired"
      :tax-rate-options="taxRateOptions"
      :is-unit-type-disabled="isUnitTypeDisabled"
      @material="handleMaterial"
    />

    <!--    查看品目信息的弹窗-->
    <detail-dialog
      v-model="showDetailDialog"
      :is-supplier="isSupplier"
      :material-info="materialInfo"
      :upload-params="defaultUploadParams"
      :material-field="tableConfig"
      title="品目信息详情"
    />
  </div>
</template>

<script>
// 组件
import MaterialItemTable from './MaterialItemTable'
import EditDialog from './EditDialog'
import DetailDialog from './DetailDialog'
import Upload from '@/components/common/Upload/index'
import DownloadFile from '@/components/common/DownloadFile'
import MoneyInCn from '@/components/common/MoneyInCn'

import { getItemPricePageApi } from '@/api/project-view/item-price'

// 字典
import { unitTaxRateDict } from '@/dictionary/project/common'

// 工具函数
import { strToThousands } from '@/utils/utils'

// 后端api接口
import { importApplyMaterialInfoApi } from '@/api/purchase-demand/apply'

export default {
  name: 'MaterialTable',

  components: {
    EditDialog,
    DetailDialog,
    Upload,
    DownloadFile,
    MaterialItemTable,
    MoneyInCn
  },
  filters: {
    priceInThousands(price) {
      if (!price) return '-'
      return strToThousands(price)
    }
  },

  props: {
    // 回显的表格数据
    tableData: {
      type: Array,
      default: () => []
    },

    /**
     * 表格表头定义函数,
     * 传入参数为JczhTableContent的option中增加字段：transProp表示原属性字段的替换字段
     */
    tableConfigFun: {
      type: Function,
      default: tableConfig => tableConfig
    },

    // 表格主键
    tableId: {
      type: String,
      default: 'spuCode'
    },

    // 是否是可编辑的
    canEdit: {
      type: Boolean,
      default: true
    },

    // 是否可以新增品目
    canAdd: {
      type: Boolean,
      default: true
    },

    // 上传组件需要的定义参数
    uploadParams: {
      type: Object,
      default: () => {
        return {
          bidType: '',
          attachmentType: ''
        }
      }
    },

    downloadLink: {
      type: String,
      default: '品目导入模板-申请单'
    },

    // 是否需要展示汇总
    showSummary: {
      type: Boolean,
      default: true
    },

    // 是否是供应商，供应商查看时隐藏单价与总价
    isSupplier: {
      type: Boolean,
      default: false
    },

    // 是否需要历史最低价
    isLowestPrice: {
      type: Boolean,
      default: false
    },

    itemCodeRequired: {
      type: Boolean,
      default: true
    },

    // 工厂编码是否不可编辑
    isFactoryDisabled: {
      type: Boolean,
      default: false
    },

    // 交货地址是否是下拉选择
    isAddressSelect: {
      type: Boolean,
      default: false
    },

    // 交货地址是否必填
    addressRequired: {
      type: Boolean,
      default: false
    },

    // 税率列表
    taxRateOptions: {
      type: Array,
      default: () => {
        return unitTaxRateDict.dictArray
      }
    },

    // 品目单位是否不可编辑
    isUnitTypeDisabled: {
      type: Boolean,
      default: false
    },

    // 已选择的品目信息
    echoList: {
      type: Array,
      default: () => {
        return []
      }
    },

    pageSizes: {
      type: Array,
      default() {
        return [10, 20, 30, 50]
      }
    }
  },

  data() {
    return {
      // 品目信息弹窗变量
      showEditDialog: false,
      editDialogTitle: '添加品目信息',
      showDetailDialog: false,

      materialList: [], // 品目列表
      materialInfo: {}, // 暂存的需要操作的品目信息
      materialIndex: -1, // 暂存的需要操作的品目信息在品目表中的下标
      tableConfig: [],
      slotList: [], // 插槽列表
      // selectedMaterials: [], // 已选择的品目信息

      uploadFile: '', // 上传的文件
      totalFee: 0, // 全部品目总价
      page: 1,
      size: 20,
      loading: false
    }
  },

  computed: {
    defaultUploadParams() {
      return this.uploadParams
    },

    totalPage() {
      return this.materialList.length / this.size
    },

    attachmentField() {
      return (
        this.slotList.filter(value => value.slotName === 'attachment')[0] || {}
      )
    },

    selectedMaterials: {
      get() {
        return this.echoList || []
      },
      set(val) {
        this.$emit('update:echoList', val || [])
      }
    }
  },

  watch: {
    materialList: {
      deep: true,
      handler: function() {
        this.getTotalFee()
      }
    },

    tableData(newVal) {
      if (newVal.length !== 0) {
        this.materialList = [...this.tableData]
      } else {
        this.materialList = []
      }
    },

    showEditDialog(val) {
      if (!val) {
        this.materialInfo = {}
      }
    }
  },

  created() {
    if (this.tableData.length !== 0) {
      this.materialList = [...this.tableData]
    }
  },

  methods: {
    /**
     * 获取并储存当前的数据字段
     */
    getTableConfigList(tableConfigList) {
      const temp = [
        ...this.tableConfigFun(tableConfigList),
        { label: '操作', slotName: 'operation', width: 160, fixed: 'right' }
      ]

      this.tableConfig = temp

      const tableConfigTemp = temp.map(value => {
        return Object.assign({}, value, { prop: value.transProp || value.prop })
      })
      this.slotList = tableConfigTemp.filter(value => value.slotName)

      return tableConfigTemp
    },

    /**
     * 添加或者编辑品目信息的函数
     * @param {string} type 表示添加还是修改
     * @param {Object} row 当type===edit时使用，需要修改的品目信息
     */
    addOrEditMaterialInfo(type, row) {
      if (type === 'add') {
        this.editDialogTitle = '添加品目信息'
      } else {
        this.editDialogTitle = '修改品目信息'
      }

      if (row) {
        this.materialInfo = row
        this.materialIndex = this.materialList.indexOf(row)
      } else {
        this.materialIndex = -1
      }

      this.showEditDialog = true
    },

    // 品目弹窗点击确定回调事件
    async handleMaterial(data) {
      // 如果暂存的需要操作的品目信息下标有值，则表示是更新操作，根据下标更新对象值
      if (this.materialIndex >= 0) {
        const tempObj = this.materialList[this.materialIndex]
        this.$set(
          this.materialList,
          this.materialIndex,
          Object.assign({}, tempObj, data, {
            tempCode:
              tempObj[this.tableId] || tempObj.tempCode || `temp-${Date.now()}`
          })
        )
      } else {
        // 其余为添加操作
        if (this.materialList.length > 999) {
          this.$message.warning('品目条数上限为999条')
          return false
        }
        const priceArr = await this.getLowestPrice(data.itemName)
        if (priceArr.length) {
          data.price = priceArr[0]
        }
        this.materialList.push({
          ...data,
          ...{ tempCode: `temp-${Date.now()}` }
        })
      }
      this.$emit('update:tableData', this.materialList)
    },

    // 查询历史最低价
    async getLowestPrice(itemName) {
      if (!itemName || !this.isLowestPrice) {
        return false
      }
      let arr = []
      await getItemPricePageApi({ itemName, page: 1, size: 10 })
        .then(res => {
          arr = res.data?.records || []
        })
        .catch(err => {
          this.$message.error(err?.message)
        })
      return arr
    },

    // 删除品目
    handleMaterialRemove(row, index) {
      this.$systemConfirm('DELETE_MATERIAL').then(result => {
        if (!result) return

        this.materialList.splice(index, 1)
        this.$message.success('删除成功')
        this.$emit('update:tableData', this.materialList)
      })
    },

    // 查看品目详情
    handleMaterialLook(row) {
      if (row) this.materialInfo = row
      this.showDetailDialog = true
    },

    // 品目表勾选项改变时触发
    handleMaterialTableSelectionChange(selection) {
      this.selectedMaterials = selection
    },

    // 批量删除
    handleBatchDelete() {
      if (this.selectedMaterials.length === 0) {
        this.$message.warning('请先选择需要删除的品目')
        return false
      }
      this.batchRemove()
    },

    // 执行删除操作
    batchRemove() {
      this.$systemConfirm('DELETE_SELECTED_MATERIAL').then(result => {
        if (!result) return

        // 获取已选项在品目列表中的下标
        const tempIndexArray = this.selectedMaterials.map(s => {
          return this.materialList.findIndex(m => m === s)
        })

        // 根据已选项的下标排除数据，返回未选的品目数据
        const tempArray = this.materialList.filter((v, index) => {
          return !tempIndexArray.includes(index)
        })
        // 将未选的品目数据覆盖写入原本的品目表，达到批量删除的目的
        this.materialList = [...tempArray]
        // this.getTotalFee()
        this.$message.success('删除成功')

        this.selectedMaterials = []
        this.$refs.materialItemTable.clearSelection()

        this.$emit('update:tableData', this.materialList)
      })
    },

    // 批量导入
    handleImport() {
      this.$refs.fileupload.click()
    },

    fileChange(e) {
      const fileList = Array.from(e.currentTarget.files)

      if (!fileList[0]) {
        return false
      }

      this.$refs.fileupload.value = ''

      importApplyMaterialInfoApi({ file: fileList[0] })
        .then(async res => {
          const tempArray = this.materialList.concat(res.data || [])
          tempArray.forEach((item, index) => {
            item.tempCode =
              item[this.tableId] ||
              item.tempCode ||
              `temp-${index}-${Date.now()}`
          })

          if (tempArray.length > 999) {
            this.$message.warning('品目条数上限为999条')
            return false
          }
          this.loading = true
          await Promise.all(
            tempArray.map(async item => {
              const priceArr = await this.getLowestPrice(item.itemName)
              if (priceArr.length) {
                item.price = priceArr[0]
              }
            })
          )
          this.loading = false
          this.materialList = tempArray
          this.$emit('update:tableData', this.materialList)
        })
        .catch(err => {
          this.$message.error(err.message || '导入失败')
        })
    },

    // 执行本地的总金额计算
    getTotalFee() {
      let fee = 0

      if (this.materialList.length === 0) {
        this.totalFee = fee
        if (this.canEdit) {
          // this.getGuaranteeFee()
          this.$emit('getTotalFee', this.totalFee)
        }
        return false
      }

      this.materialList.forEach(val => {
        fee = fee + Number(val.totalPrice) * 100
      })

      const tempFee = fee / 100
      this.totalFee = strToThousands(String(tempFee))
      // if (this.canEdit) this.getGuaranteeFee()
      if (this.canEdit) {
        this.$emit('getTotalFee', tempFee)
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.table-btn {
  height: 32px;
  border: 1px dashed $primary-color;
  color: $primary-color;
  font-size: 14px;
  cursor: pointer;
  width: 100%;
  margin: 0 auto;
  background-color: $white-background-color;
  border-radius: 4px;

  i {
    @include text-bold;
    font-size: 14px;
    margin-right: 4px;
  }

  &:hover {
    opacity: 0.8;
  }
}

.all-count {
  background-color: $info-fifth-color;
  border: 1px solid $info-fourth-color;
  border-radius: 4px;
  height: 40px;
  line-height: 40px;
  padding: 0 16px;
  display: inline-block;

  .all-count__label {
    color: $second-text-color;
    font-size: 14px;
    &::before {
      display: inline-block;
      content: '*';
      color: $danger-color;
      margin-right: 4px;
    }
  }

  .all-count__content {
    color: $text-color;
    font-size: 16px;
  }
  .all-count__unit {
    font-size: 14px;
    color: $second-text-color;
    padding-left: 10px;
  }
  .all-count__content_capitalize {
    color: $danger-color;
    padding-left: 16px;
  }
}
</style>
